import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { Checkbox } from '@/components/ui/checkbox';

import { toast } from '@/hooks/use-toast';
import { GraduationCap, Save, BookOpen, Search, Download, TrendingUp, Plus, Lock, AlertCircle, Trash2, Users } from 'lucide-react';
import ProjectedResults from "../../Teacherdashboard/ProjectedResults";
import ContentLoader from "../../authentication/ContentLoader";
import { db, auth } from "../../firebase/config";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  query,
  where,
  writeBatch,
  serverTimestamp,
  DocumentReference,
  addDoc,
  updateDoc,
} from "firebase/firestore";
import { onAuthStateChanged, getIdTokenResult, IdTokenResult } from "firebase/auth";

interface Student {
  id: string;
  name: string;
  regNumber: string;
  marks: number | null;
  grade: string;

  formClass?: string;
  currentClassUid?: string;
  enrolledSubjects?: string[];
  gradeDocId?: string;
  form?: string;
  isIncomplete?: boolean;
}

interface StudentGroup {
  id: string;
  name: string;
  subject: string;
  form: string;
  studentIds: string[];
}

interface ClassItem {
  id: string;
  name: string;
  classUid: string;
  level: number;
  stream: string;
}

interface Subject {
  subjectId: string;
  name: string;
  code: string;
}

interface GradeBoundary {
  grade: string;
  min: number;
  max: number;
}

interface GradeScheme {
  id: string;
  name: string;
  type: "percentage";
  boundaries: GradeBoundary[];
  passThreshold: number;
  isActive: boolean;
}

interface ExamType {
  id: string;
  name: string;
  weight: number;
  status: "active" | "inactive";
  description?: string;
}

interface ExamSession {
  id: string;
  name: string;
  type: "regular" | "mock" | "custom";
  forms: number[];
  status: "active" | "inactive";
  startDate?: string;
  endDate?: string;
  examTypes: ExamType[];
  submissionAllowed: boolean;
  createdAt?: any;
  updatedAt?: any;
}

interface Affiliation {
  role: string;
  schoolID?: string;
}

interface CustomClaims {
  affiliations?: Affiliation[];
  defaultAffiliation?: Affiliation;
  schoolID?: string;
}

interface ExamCategory {
  id: string;
  name: string;
  subjectId: string;
  maxMarks: number;
  weight?: number;
  examTypeId?: string;
  examSessionId?: string;
  createdAt?: any;
  updatedAt?: any;
}

type GradingMode = 'class' | 'subject' | 'projected';

interface SharedGradingDashboardProps {
  schoolID?: string;
  userRole?: 'teacher' | 'admin' | 'principal';
  defaultClassId?: string;
}

const SharedGradingDashboard = ({ schoolID: propSchoolID, userRole = 'teacher', defaultClassId }: SharedGradingDashboardProps) => {
  const navigate = useNavigate();
  
  // Suspension check removed from shared component - should be handled by parent if needed

  
  const [gradingMode, setGradingMode] = useState<GradingMode>('class');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedExamType, setSelectedExamType] = useState('');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [students, setStudents] = useState<Student[]>([]);
  const [isTermClosed, setIsTermClosed] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Subject Grading Mode States
  const [selectedForm, setSelectedForm] = useState('');
  const [allFormStudents, setAllFormStudents] = useState<Student[]>([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState<string[]>([]);
  const [savedGroups, setSavedGroups] = useState<StudentGroup[]>([]);
  const [groupSearchQuery, setGroupSearchQuery] = useState('');
  const filteredSavedGroups = useMemo(() => {
    const q = (groupSearchQuery || '').trim().toLowerCase();
    if (!q) return savedGroups;
    return savedGroups.filter(g => (
      (g.name || '').toLowerCase().includes(q) ||
      (g.subject || '').toLowerCase().includes(q) ||
      (g.form || '').toLowerCase().includes(q)
    ));
  }, [savedGroups, groupSearchQuery]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [showGroupDialog, setShowGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Firebase states
  const [classes, setClasses] = useState<ClassItem[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [forms, setForms] = useState<string[]>([]);
  const [activeGradeScheme, setActiveGradeScheme] = useState<GradeScheme | null>(null);
  const [sessions, setSessions] = useState<ExamSession[]>([]);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [hasValidClaims, setHasValidClaims] = useState(false);
  const [schoolID, setSchoolID] = useState("");
  const [submittedBy, setSubmittedBy] = useState("");
  const [selectedSession, setSelectedSession] = useState<ExamSession | null>(null);

  // Additional states for IDs
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [selectedExamTypeId, setSelectedExamTypeId] = useState('');
  const [systemTermDates, setSystemTermDates] = useState<any>(null);
  const [useRecommendedDates, setUseRecommendedDates] = useState<boolean>(false);


  const [schoolTerms, setSchoolTerms] = useState<any[]>([]);

  // Use defaultClassId if provided
  useEffect(() => {
    if (defaultClassId && classes.length > 0) {
      const cls = classes.find(c => c.id === defaultClassId);
      if (cls) {
        setSelectedClass(cls.name);
      }
    }
  }, [defaultClassId, classes]);

  // Loading state
  const [isFetching, setIsFetching] = useState(false);
  const [categories, setCategories] = useState<ExamCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [tempCategories, setTempCategories] = useState<{ id?: string; name: string; maxMarks: number; weight: number }[]>([]);



  // Derived weights for category allocation within an exam type
  const selectedExamTypeWeight = useMemo(() => {
    const et = selectedSession?.examTypes.find(e => e.id === selectedExamTypeId);
    return et?.weight ?? 100;
  }, [selectedSession, selectedExamTypeId]);

  const totalCategoryMax = useMemo(() => {
    return categories.reduce((sum, cat) => sum + (cat.maxMarks || 0), 0);
  }, [categories]);


  // Fetch states to control empty messages
  const [hasFetchedStudents, setHasFetchedStudents] = useState(false);
  const [hasFetchedFormStudents, setHasFetchedFormStudents] = useState(false);
  const [isLoadingGrades, setIsLoadingGrades] = useState(false);

  // Academic years
  const years = useMemo(() => {
    const current = 2025;
    return Array.from({ length: 5 }, (_, i) => (current - 2 + i).toString());
  }, []);

  // Fetch system term dates from systemConfig/global
  useEffect(() => {
    const fetchSystemTermDates = async () => {
      try {
        const configRef = doc(db, "systemConfig/global");
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
          const data = configSnap.data();
          setSystemTermDates(data.termDates || null);
        }
      } catch (error) {
        console.error("Error fetching system term dates:", error);
      }
    };
    fetchSystemTermDates();
  }, []);

  // Helper to determine current term based on today's date and term date ranges
  const getCurrentTerm = useCallback((sessions: ExamSession[], systemDates: any, useRecommended: boolean, schoolTerms: any[]): string | null => {
    const today = new Date();
    const currentYear = today.getFullYear();
    
    // Helper to parse date string to Date object
    const parseDate = (dateStr: string): Date | null => {
      if (!dateStr) return null;
      // Handle YYYY-MM-DD
      if (dateStr.includes('-') && dateStr.length === 10) {
        return new Date(dateStr);
      }
      // Handle MM-DD (assume current year)
      if (dateStr.includes('-') && dateStr.length <= 5) {
        const [m, d] = dateStr.split('-').map(Number);
        return new Date(currentYear, m - 1, d);
      }
      return null;
    };

    let term1Start, term1End, term2Start, term2End, term3Start, term3End;

    if (useRecommended && systemDates) {
       term1Start = parseDate(systemDates.term1Start);
       term1End = parseDate(systemDates.term1End);
       term2Start = parseDate(systemDates.term2Start);
       term2End = parseDate(systemDates.term2End);
       term3Start = parseDate(systemDates.term3Start);
       term3End = parseDate(systemDates.term3End);
    } else if (schoolTerms.length > 0) {
       // Find terms by number or title
       const t1 = schoolTerms.find(t => t.number === 1 || t.title?.toLowerCase().includes('term 1'));
       const t2 = schoolTerms.find(t => t.number === 2 || t.title?.toLowerCase().includes('term 2'));
       const t3 = schoolTerms.find(t => t.number === 3 || t.title?.toLowerCase().includes('term 3'));
       
       if (t1) { term1Start = parseDate(t1.openingDate); term1End = parseDate(t1.closingDate); }
       if (t2) { term2Start = parseDate(t2.openingDate); term2End = parseDate(t2.closingDate); }
       if (t3) { term3Start = parseDate(t3.openingDate); term3End = parseDate(t3.closingDate); }
    }

    // Fallback if no dates found
    if (!term1Start) {
       const month = today.getMonth() + 1;
       if (month <= 4) return sessions.find(s => s.name === 'TERM 1')?.name || null;
       if (month <= 8) return sessions.find(s => s.name === 'TERM 2')?.name || null;
       return sessions.find(s => s.name === 'TERM 3')?.name || null;
    }

    const checkTerm = (start: Date | undefined | null, end: Date | undefined | null) => {
        if (!start || !end) return false;
        // Reset time for accurate comparison
        const s = new Date(start); s.setHours(0,0,0,0);
        const e = new Date(end); e.setHours(23,59,59,999);
        const t = new Date(today); t.setHours(12,0,0,0);
        return t >= s && t <= e;
    };

    if (checkTerm(term1Start, term1End)) return sessions.find(s => s.name === 'TERM 1')?.name || null;
    if (checkTerm(term2Start, term2End)) return sessions.find(s => s.name === 'TERM 2')?.name || null;
    if (checkTerm(term3Start, term3End)) return sessions.find(s => s.name === 'TERM 3')?.name || null;

    // Handle breaks/holidays (gaps between terms)
    // If we are past Term 3 end (December holiday), default to Term 3 (grading/reports)
    if (term3End && today > term3End) return sessions.find(s => s.name === 'TERM 3')?.name || null;
    
    // If between Term 2 and Term 3, prepare for Term 3
    if (term2End && today > term2End) return sessions.find(s => s.name === 'TERM 3')?.name || null;

    // If between Term 1 and Term 2, prepare for Term 2
    if (term1End && today > term1End) return sessions.find(s => s.name === 'TERM 2')?.name || null;

    // If before Term 1 start, prepare for Term 1
    return sessions.find(s => s.name === 'TERM 1')?.name || null;
  }, []);

  // Reset states on grading mode change
  useEffect(() => {
    setStudents([]);
    setIsSubmitted(false);
    setCurrentPage(1);
    setHasFetchedStudents(false);
    setHasFetchedFormStudents(false);
    if (gradingMode === 'class') {
      setAllFormStudents([]);
      setSelectedStudentIds([]);
      setSelectedForm('');
      setSearchQuery('');
      setSelectedGroup('');
    } else {
      setSelectedClass('');
      setSelectedTerm('');
      setSelectedExamType('');
    }
  }, [gradingMode]);

  // Fetch data after auth
  useEffect(() => {
    if (!schoolID || !hasValidClaims) return;

    const fetchData = async () => {
      setIsFetching(true);
      try {
        // Fetch classes
        const classesCollection = collection(db, `schools/${schoolID}/classes`);
        const classesSnapshot = await getDocs(classesCollection);
        const classesData: ClassItem[] = classesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          classUid: doc.data().classUid || doc.id,
        } as ClassItem)).filter(c => c.name && c.name.trim() !== "");

        setClasses(classesData);

        // Derive forms
        const extractFormLevel = (className: string): string => {
          const parts = className.trim().toLowerCase().split(/\s+/);
          if (parts.length >= 3 && parts[0] === 'pre' && parts[1] === 'primary') {
            return className.split(/\s+/).slice(0, 3).join(' ');
          } else if (parts.length >= 2 && parts[0] === 'form') {
            return className.split(/\s+/).slice(0, 2).join(' ');
          } else {
            return className.split(/\s+/).slice(0, 1).join(' ');
          }
        };
        const uniqueForms = [...new Set(classesData.map(c => extractFormLevel(c.name)))].sort((a, b) => a.localeCompare(b));
        setForms(uniqueForms);

        // Fetch subjects
        const subjectsCollection = collection(db, `schools/${schoolID}/subjects`);
        const subjectsSnapshot = await getDocs(subjectsCollection);
        const subjectsData: Subject[] = subjectsSnapshot.docs.map((doc) => ({
          subjectId: doc.id,
          ...doc.data(),
        } as Subject)).filter(s => s.name && s.name.trim() !== "");

        setSubjects(subjectsData);

        // Fetch active grading scheme
        const schemesCollection = collection(db, `schools/${schoolID}/gradingSchemes`);
        const schemesSnapshot = await getDocs(schemesCollection);
        const schemesData = schemesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as GradeScheme[];

        const activeScheme = schemesData.find(s => s.isActive);
        setActiveGradeScheme(activeScheme || null);

        // Fetch sessions
        const sessionsCollection = collection(db, `schools/${schoolID}/examSessions`);
        const sessionsSnapshot = await getDocs(sessionsCollection);
        const sessionsData: ExamSession[] = sessionsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        } as ExamSession));

        setSessions(sessionsData);

        // Fetch settings
        const settingsRef = doc(db, `schools/${schoolID}/settings/academics`);
        const settingsSnap = await getDoc(settingsRef);
        if (settingsSnap.exists()) {
          setUseRecommendedDates(settingsSnap.data().useRecommendedDates || false);
        }

        // Fetch school terms
        const termsRef = collection(db, `schools/${schoolID}/terms`);
        const termsSnap = await getDocs(termsRef);
        const termsData = termsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSchoolTerms(termsData);

      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Data Fetch Failed",
          description: "Unable to load classes, subjects, or grading scheme.",
          variant: "destructive",
          duration: 5000,
          
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [schoolID, hasValidClaims, toast]);

  const activeSessions = useMemo(() => sessions.filter(s => s.status === 'active'), [sessions]);

  // Auto-select current term when sessions are available (even if systemTermDates is null, we use fallback)
  useEffect(() => {
    if (!isFetching && activeSessions.length > 0) {
      // If using recommended dates, wait for system dates to load to avoid premature fallback
      if (useRecommendedDates && !systemTermDates) return;

      if (selectedTerm === '') {
        // Pass systemTermDates (could be null) to getCurrentTerm which now handles nulls
        const currentTerm = getCurrentTerm(activeSessions, systemTermDates, useRecommendedDates, schoolTerms);
        if (currentTerm) {
          setSelectedTerm(currentTerm);
        }
      }
    }
  }, [activeSessions, systemTermDates, selectedTerm, getCurrentTerm, useRecommendedDates, schoolTerms, isFetching]);

  // Update selected session and term closed status
  useEffect(() => {
    const currentSession = activeSessions.find(s => s.name === selectedTerm);
    setSelectedSession(currentSession || null);
    if (currentSession) {
      const examTypeStillValid = currentSession.examTypes.some(et => et.id === selectedExamTypeId || et.name === selectedExamType);
      if (!examTypeStillValid) {
        setSelectedExamType('');
        setSelectedExamTypeId('');
      }
    } else {
      setSelectedExamType('');
      setSelectedExamTypeId('');
    }
    if (currentSession) {
      const currentDate = new Date();
      const endDateClosed = currentSession.endDate ? new Date(currentSession.endDate) < currentDate : false;
      // Check if submission is not allowed (toggled off in exam session management)
      const submissionDisabled = currentSession.submissionAllowed === false;
      const isClosed = currentSession.status !== 'active' || endDateClosed || submissionDisabled;
      setIsTermClosed(isClosed);
    } else {
      setIsTermClosed(false);
    }
  }, [selectedTerm, activeSessions, selectedExamTypeId, selectedExamType]);

  // Authentication and claims verification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAuthLoading(false);
        return;
      }

      if (propSchoolID) {
         setSchoolID(propSchoolID);
         setHasValidClaims(true); 
         setSubmittedBy(user.uid);
         setIsAuthLoading(false);
         // If props provide schoolID, we trust the parent component (Admin/Teacher wrapper) handled auth
         return;
      }

      setSubmittedBy(user.uid);
      try {
        const idTokenResult: IdTokenResult = await getIdTokenResult(user, true);
        const claims = idTokenResult.claims as CustomClaims;
        const affiliations: Affiliation[] = claims.affiliations || [];
        const defaultAff: Affiliation | undefined = claims.defaultAffiliation;
        const claimSchoolID: string | undefined = claims.schoolID;

        let selectedAffiliation: Affiliation | null = null;
        if (defaultAff && defaultAff.role === "school_staff" && defaultAff.schoolID) {
          selectedAffiliation = defaultAff;
        } else if (affiliations.length > 0) {
          selectedAffiliation = affiliations.find(aff => aff.role === "school_staff" && aff.schoolID) || affiliations[0];
        }

        if (selectedAffiliation?.role === "school_staff" && selectedAffiliation.schoolID && claimSchoolID === selectedAffiliation.schoolID) {
          setSchoolID(selectedAffiliation.schoolID);
          setHasValidClaims(true);
        } else {
          await new Promise((resolve) => setTimeout(resolve, 10000));
          await user.getIdToken(true);
          const retryIdTokenResult: IdTokenResult = await getIdTokenResult(user, true);
          const retryClaims = retryIdTokenResult.claims as CustomClaims;
          const retryAffiliations: Affiliation[] = retryClaims.affiliations || [];
          const retryDefaultAff: Affiliation | undefined = retryClaims.defaultAffiliation;
          const retrySchoolID: string | undefined = retryClaims.schoolID;

          let retrySelectedAffiliation: Affiliation | null = null;
          if (retryDefaultAff && retryDefaultAff.role === "school_staff" && retryDefaultAff.schoolID) {
            retrySelectedAffiliation = retryDefaultAff;
          } else if (retryAffiliations.length > 0) {
            retrySelectedAffiliation = retryAffiliations.find(aff => aff.role === "school_staff" && aff.schoolID) || retryAffiliations[0];
          }

          if (retrySelectedAffiliation?.role === "school_staff" && retrySelectedAffiliation.schoolID && retrySchoolID === retrySelectedAffiliation.schoolID) {
            setSchoolID(retrySelectedAffiliation.schoolID);
            setHasValidClaims(true);
          } else {
            setHasValidClaims(false);
            setSchoolID("");
            toast({
              title: "Access Denied",
              description: "Invalid teacher permissions. Contact admin.",
              variant: "destructive",
              duration: 5000,
            });
          }
        }
      } catch (error: any) {
        console.error("Claims validation error:", error);
          toast({
    title: "Authentication Error",
    description: "Failed to verify permissions. Please log in again.",
    variant: "destructive",
    duration: 5000,
    
        });
        setHasValidClaims(false);
        setSchoolID("");
      }
      setIsAuthLoading(false);
    });

    return () => unsubscribe();
  }, [toast]);

  // Generate deterministic document ID for grade records
  // This ensures only ONE grade document can exist per unique combination
  const generateGradeDocId = useCallback((
    studentId: string,
    subjectId: string,
    examSessionId: string,
    examTypeId: string,
    categoryId: string,
    academicYear: string
  ): string => {
    return `${studentId}_${subjectId}_${examSessionId}_${examTypeId}_${categoryId}_${academicYear}`;
  }, []);

  const calculateGrade = useCallback((marks: number | null): string => {
    if (marks === null || marks < 0) return 'N/A';
    if (!activeGradeScheme?.boundaries) return 'N/A';

    const sortedBoundaries = [...activeGradeScheme.boundaries].sort((a, b) => b.min - a.min);
    for (const boundary of sortedBoundaries) {
      if (marks >= boundary.min && marks <= boundary.max) {
        return boundary.grade;
      }
    }
    return 'N/A';
  }, [activeGradeScheme]);



  // Check for incomplete marks (some categories graded but not all)
  const checkCompletionStatus = useCallback(async (studentIds: string[], subjectId: string, sessionId: string, examTypeId: string, allCategories: ExamCategory[]) => {
      console.log('[checkCompletionStatus] Called with:', { studentIds: studentIds.length, subjectId, sessionId, examTypeId, categoriesCount: allCategories.length });
      
      if (!studentIds.length || !subjectId || !sessionId || !examTypeId || allCategories.length <= 1) {
        console.log('[checkCompletionStatus] Early return - conditions not met:', { 
          hasStudents: studentIds.length > 0, 
          hasSubject: !!subjectId, 
          hasSession: !!sessionId, 
          hasExamType: !!examTypeId, 
          categoriesCount: allCategories.length 
        });
        return;
      }
      
      const totalCategories = allCategories.length;
      console.log('[checkCompletionStatus] Total categories:', totalCategories);

      try {
         const gradesCollection = collection(db, `schools/${schoolID}/grades`);
         const studentGradesMap: Record<string, Set<string>> = {}; 

         // Query in batches
         for (let i = 0; i < studentIds.length; i += 10) {
             const batchIds = studentIds.slice(i, i + 10);
             const q = query(
                gradesCollection,
                where("studentId", "in", batchIds),
                where("subjectId", "==", subjectId),
                where("examSessionId", "==", sessionId),
                where("examTypeId", "==", examTypeId)
             );
             
             const snapshot = await getDocs(q);
             console.log(`[checkCompletionStatus] Batch ${i}: Found ${snapshot.size} grade documents`);
             
             snapshot.forEach(doc => {
                 const data = doc.data();
                 const sId = data.studentId;
                 const cId = data.categoryId;
                 if (data.marks !== null && data.marks !== undefined) {
                     if (!studentGradesMap[sId]) studentGradesMap[sId] = new Set();
                     if (cId) studentGradesMap[sId].add(cId);
                 }
             });
         }

         console.log('[checkCompletionStatus] Student grades map:', Object.fromEntries(
           Object.entries(studentGradesMap).map(([k, v]) => [k, Array.from(v)])
         ));

         setStudents(prev => prev.map(s => {
             const gradedCount = studentGradesMap[s.id]?.size || 0;
             const isIncomplete = gradedCount > 0 && gradedCount < totalCategories;
             
             if (isIncomplete) {
               console.log(`[checkCompletionStatus] Student ${s.name} is INCOMPLETE: ${gradedCount}/${totalCategories} categories`);
             }
             
             return { ...s, isIncomplete };
         }));

      } catch (err) {
          console.error("[checkCompletionStatus] Error:", err);
      }
  }, [schoolID]);

  // Ref to skip the useEffect grade fetch when we've already loaded grades (e.g., in handleLoadGroup)
  const skipGradesFetchRef = useRef(false);
  
  // Ref to track the current fetch request ID to cancel stale requests
  const fetchRequestIdRef = useRef(0);

  const fetchExistingGrades = useCallback(async (studentIds: string[], subjectId: string, sessionId: string, examTypeId: string, categoryId?: string, suppressToast: boolean = false, requestId?: number) => {
    const targetCategory = categoryId || selectedCategory;
    if (studentIds.length === 0 || !schoolID || !targetCategory) return;

    const existing: { [key: string]: any } = {};

    try {
      // PRIMARY METHOD: Fetch by deterministic document ID (fast, precise)
      // This is the preferred method for new data
      for (const studentId of studentIds) {
        const gradeDocId = generateGradeDocId(
          studentId,
          subjectId,
          sessionId,
          examTypeId,
          targetCategory,
          selectedYear
        );
        
        const docRef = doc(db, `schools/${schoolID}/grades`, gradeDocId);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const grade = data.grade || (data.marks ? calculateGrade(data.marks) : 'N/A');


          existing[studentId] = {
            id: docSnap.id,
            marks: data.marks ?? null,
            grade: grade,
            submitted: !data.isDraft,
            isDraft: data.isDraft ?? false,
            updatedAt: data.updatedAt || null,
            submittedAt: data.submittedAt || null
          };
        }
      }

      // FALLBACK: Query for legacy data with auto-generated IDs
      // Only for students not found above
      const remainingStudents = studentIds.filter(id => !existing[id]);
      if (remainingStudents.length > 0) {
        const gradeCollection = collection(db, `schools/${schoolID}/grades`);
        
        for (let i = 0; i < remainingStudents.length; i += 10) {
          const batchIds = remainingStudents.slice(i, i + 10);
          
          // Query with academicYear filter
          const legacyQuery = query(
            gradeCollection,
            where("studentId", "in", batchIds),
            where("subjectId", "==", subjectId),
            where("examSessionId", "==", sessionId),
            where("examTypeId", "==", examTypeId),
            where("categoryId", "==", targetCategory),
            where("academicYear", "==", selectedYear)
          );
          const legacySnapshot = await getDocs(legacyQuery);
          
          legacySnapshot.forEach((docSnap) => {
            const data = docSnap.data();
            const studentId = data.studentId;
            
            // Only use if we don't already have this student
            if (!existing[studentId]) {
              const grade = data.grade || (data.marks ? calculateGrade(data.marks) : 'N/A');
              existing[studentId] = {
                id: docSnap.id,
                marks: data.marks ?? null,
                grade: grade,
                submitted: !data.isDraft,
                isDraft: data.isDraft ?? false,
                updatedAt: data.updatedAt || null,
                submittedAt: data.submittedAt || null
              };
            }
          });
        }
      }

      // Update students with their grades
      setStudents((prev) =>
        prev.map((s) => {
          const eg = existing[s.id];
          if (eg) {
            return {
              ...s,
              gradeDocId: eg.id,
              marks: eg.marks,
              grade: eg.grade,

            };
          }
          return {
            ...s,
            gradeDocId: undefined,
            marks: null,
            grade: 'N/A',

          };
        })
      );

      // Check if this request is still valid (not stale)
      if (requestId !== undefined && requestId !== fetchRequestIdRef.current) {
        // This is a stale request, ignore the results
        return;
      }

      // Always keeping UI in edit mode
      setIsSubmitted(false);

      // Show appropriate notification
      const categoryName = categories.find(c => c.id === selectedCategory)?.name || 'this category';
      
      // Only show toast notifications if not suppressed (e.g., when called from handleLoadGroup)
      if (!suppressToast && Object.keys(existing).length > 0) {
          toast({
            title: "Grades Loaded",
            description: `Existing grades for ${categoryName} have been loaded.`,
            variant: "default",
          });
      }
    } catch (error) {
      console.error("Error fetching grades:", error);
      toast({
        title: "Error Loading Grades",
        description: "Failed to load existing grades. Please try again.",
        variant: "destructive",
        
      });
    }
  }, [schoolID, calculateGrade, selectedCategory, categories, generateGradeDocId, selectedYear]);

  // Keep a ref to fetchExistingGrades to avoid adding it as a useEffect dependency
  const fetchExistingGradesRef = useRef(fetchExistingGrades);
  useEffect(() => {
    fetchExistingGradesRef.current = fetchExistingGrades;
  }, [fetchExistingGrades]);

  const saveGrades = useCallback(async () => {
    if (!selectedSession || !selectedExamTypeId || !selectedSubjectId || !selectedCategory) {
      toast({
        title: "Cannot Save Grades",
        description: "Please ensure you've selected a category and all required fields.",
        variant: "destructive",
      });
      throw new Error("Missing required selection data.");
    }

    const batch = writeBatch(db);
    const newRefs: { ref: DocumentReference, studentId: string }[] = [];

    // Filter out students with no marks to save?
    // Actually, user might want to clear a grade by deleting text. 
    // Implementation details: invalid number logic exists in updateStudentMarks.
    // Here we save whatever is in the state.
    
    for (const student of students) {
      // If marks are null and it is not a delete operation, we might just skip creating a doc?
      // But if a doc exists and marks are now null, it should probably be deleted or updated to null.
      // Current logic: calculateGrade(null) -> 'N/A'.
      // Only skip if there are absolutely no changes? 
      // The original code skipped null marks if '!isDraft'. Now we save everything.
      
      // OPTIONAL: Skip students who have NO marks and NO existing doc to avoid clutter, 
      // BUT if we want to support "saving a blank state", we should proceed.
      // For now, let's behave like "Draft" used to: save whatever is there.
      
      const category = categories.find(c => c.id === selectedCategory);
      const categoryMax = category?.maxMarks || 100;
      
      let categoryWeight: number;
      if (category?.weight !== undefined) {
        categoryWeight = Number(((category.weight / 100) * selectedExamTypeWeight).toFixed(2));
      } else {
        const categoryShare = totalCategoryMax > 0 ? (categoryMax / totalCategoryMax) : 0;
        categoryWeight = Number((selectedExamTypeWeight * categoryShare).toFixed(2));
      }

      const normalizedExamScore = student.marks === null
        ? null
        : Number(((student.marks / categoryMax) * categoryWeight).toFixed(2));

      const gradeData: any = {
        studentId: student.id,
        subjectId: selectedSubjectId,
        examSessionId: selectedSession.id,
        examTypeId: selectedExamTypeId,
        categoryId: selectedCategory,
        categoryName: category?.name || '',
        marks: student.marks,
        normalizedExamScore,
        categoryWeight,
        grade: student.grade,

        submittedBy,
        academicYear: selectedYear,
        studentClassUid: student.currentClassUid,
        className: student.formClass,
        form: student.form || student.formClass?.split(' ')[1] || '',
        // Always save as NOT draft so it shows in reports
        isDraft: false,
        updatedAt: serverTimestamp(),
        submittedAt: serverTimestamp(),
      };

      const gradeDocId = generateGradeDocId(
        student.id,
        selectedSubjectId,
        selectedSession.id,
        selectedExamTypeId,
        selectedCategory,
        selectedYear
      );
      
      const gradeRef = doc(db, `schools/${schoolID}/grades`, gradeDocId);
      
      batch.set(gradeRef, gradeData, { merge: true });
      
      if (!student.gradeDocId || student.gradeDocId !== gradeDocId) {
        newRefs.push({ ref: gradeRef, studentId: student.id });
      }
    }

    try {
      await batch.commit();

      setStudents((prev) =>
        prev.map((s) => {
          const newRef = newRefs.find((nr) => nr.studentId === s.id);
          if (newRef) {
            return { ...s, gradeDocId: newRef.ref.id };
          }
          return s;
        })
      );

      // Do NOT set isSubmitted(true). We want to keep editing enabled.
      // setIsSubmitted(true); 

      if (categories.length > 1) {
          checkCompletionStatus(
            students.map(s => s.id),
            selectedSubjectId,
            selectedSession.id,
            selectedExamTypeId,
            categories
          );
      }

      return true;
    } catch (error) {
      console.error("Error saving grades:", error);
      throw error;
    }
  }, [selectedSession, selectedExamTypeId, selectedSubjectId, selectedYear, students, schoolID, submittedBy, categories, selectedCategory, totalCategoryMax, selectedExamTypeWeight, generateGradeDocId, checkCompletionStatus]);

  useEffect(() => {
    const loadGrades = async () => {
      // Skip if we've already loaded grades explicitly (e.g., in handleLoadGroup)
      if (skipGradesFetchRef.current) {
        skipGradesFetchRef.current = false;
        return;
      }

      // Only attempt to load grades if we have all required criteria and students
      if (
        students.length > 0 &&
        selectedSubjectId &&
        selectedSession?.id &&
        selectedExamTypeId &&
        selectedCategory
      ) {
        // Create a unique request ID to track this fetch
        const currentRequestId = ++fetchRequestIdRef.current;
        
        try {
          // Set loading state for smooth transition (don't reset grades yet)
          setIsLoadingGrades(true);
          // Don't set isSubmitted(false) here - let fetchExistingGrades set it after data loads

          // Load grades for the new category - this will update students state internally
          await fetchExistingGradesRef.current(
            students.map((s) => s.id),
            selectedSubjectId,
            selectedSession.id,
            selectedExamTypeId,
            undefined,
            false,
            currentRequestId // Pass request ID to detect stale requests
          );
        } catch (error) {
          console.error('Error loading grades:', error);
          // Only reset grades on error
          setStudents(prev => 
            prev.map(student => ({
              ...student,
              marks: null,
              grade: 'N/A',

              gradeDocId: undefined
            }))
          );
          toast({
            title: "Error Loading Grades",
            description: "Could not load saved grades. Please try again.",
            variant: "destructive",
          });
        } finally {
          setIsLoadingGrades(false);
        }
      } else if (!selectedCategory && students.length > 0) {
        // Only reset when category is deselected (not during transition)
        setIsSubmitted(false);
        setStudents(prev =>
          prev.map(student => ({
            ...student,
            marks: null,
            grade: 'N/A',

            gradeDocId: undefined
          }))
        );
      }
    };

    // Add a small delay to avoid multiple rapid fetches
    const timeoutId = setTimeout(loadGrades, 100);
    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSubjectId,
    selectedSession?.id,
    selectedExamTypeId,
    selectedCategory,
    students.length
    // Note: fetchExistingGrades is intentionally excluded - we use fetchExistingGradesRef instead
    // to prevent double triggers when the callback reference changes with selectedCategory
  ]);

  useEffect(() => {
    if (gradingMode === 'class' && (selectedClass || selectedSubject)) {
      setStudents([]);
      setIsSubmitted(false);
      setHasFetchedStudents(false);
    }
  }, [selectedClass, selectedSubject, gradingMode]);

  useEffect(() => {
    if (gradingMode === 'subject' && (selectedForm || selectedSubject)) {
      setAllFormStudents([]);
      setSelectedStudentIds([]);
      setSearchQuery('');
      setStudents([]);
      setIsSubmitted(false);
      setHasFetchedFormStudents(false);
    }
  }, [selectedForm, selectedSubject, gradingMode]);



  const handleFetchStudents = async () => {
    if (!selectedClass || !selectedSubject || !selectedTerm || !selectedExamType) {
      toast({
        title: "Missing Information",
        description: "Please select all required fields.",
        variant: "destructive",
        
      });
      return;
    }

    const selectedSubjectItem = subjects.find(s => s.name === selectedSubject);
    if (!selectedSubjectItem) {
      toast({
        title: "Subject Not Found",
        description: "Selected subject not found.",
        variant: "destructive",
        
      });
      return;
    }
    const targetSubjectId = selectedSubjectItem.subjectId;

    const selectedClassItem = classes.find(c => c.name === selectedClass);
    if (!selectedClassItem) {
      toast({
        title: "Class Not Found",
        description: "Selected class not found.",
        variant: "destructive",
        
      });
      return;
    }

    setStudents([]);
    setIsSubmitted(false);
    setCurrentPage(1);

    setIsFetching(true);

    try {
      const studentsCollection = collection(db, `schools/${schoolID}/students`);
      const studentsQuery = query(studentsCollection, where("currentClassUid", "==", selectedClassItem.classUid));
      const studentsSnapshot = await getDocs(studentsQuery);

      const rawStudents: Student[] = [];
      studentsSnapshot.docs.forEach((doc) => {
        const studentData = doc.data();
        const student: Student = {
          id: doc.id,
          name: `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim(),
          regNumber: studentData.registrationNumber || '',
          marks: null,
          grade: 'N/A',

          formClass: selectedClass,
          currentClassUid: studentData.currentClassUid,
          enrolledSubjects: studentData.enrolledSubjects || [],
          form: selectedClass.split(' ')[1] || '',
        };
        rawStudents.push(student);
      });

      const studentsData = rawStudents.filter(s =>
        s.name &&
        s.regNumber &&
        s.enrolledSubjects?.includes(targetSubjectId)
      );

      setStudents(studentsData);
      setHasFetchedStudents(true);

      // After setting students, load any existing grades for this category
      if (studentsData.length > 0) {
          // 1. Load grades for CURRENTLY SELECTED category (for display)
          if (selectedCategory) {
            await fetchExistingGrades(
                studentsData.map(s => s.id),
                targetSubjectId,
                selectedSession?.id || '',
                selectedExamTypeId || ''
            );
          }
          
          // 2. Check completion status across ALL categories
          console.log('[handleFetchStudents] Categories at completion check time:', categories.length, categories.map(c => c.name));
          if (categories.length > 1) {
             console.log('[handleFetchStudents] Calling checkCompletionStatus...');
             checkCompletionStatus(
                studentsData.map(s => s.id),
                targetSubjectId,
                selectedSession?.id || '',
                selectedExamTypeId || '',
                categories
             );
          } else {
             console.log('[handleFetchStudents] Skipping checkCompletionStatus - only', categories.length, 'categories');
          }
      }

      toast({
        title: "Students Loaded",
        description: `Loaded ${studentsData.length} enrolled students for ${selectedClass} - ${selectedSubject}.`,
        variant: "default",
        
      });
    } catch (error) {
      console.error("Error fetching students:", error);
      toast({
        title: "Fetch Failed",
        description: "Unable to load students. Check console for details.",
        variant: "destructive",
        
      });
    } finally {
      setIsFetching(false);
    }
    return allFormStudents;
  };



  const updateStudentMarks = (studentId: string, marks: string) => {
    const numMarks = marks === '' ? null : Number(marks);
    const maxMarks = categories.find(c => c.id === selectedCategory)?.maxMarks || 100;

    if (numMarks !== null && (numMarks < 0 || numMarks > maxMarks)) {
      toast({
        title: "Invalid Marks",
        description: `Marks must be between 0 and ${maxMarks}.`,
        variant: "destructive",
        
      });
      return;
    }

    setStudents(prev => prev.map(student => {
      if (student.id === studentId) {
        const grade = calculateGrade(numMarks);
        return { ...student, marks: numMarks, grade };
      }
      return student;
    }));
  };



  const handleSave = async () => {
    if (isTermClosed) {
      toast({
        title: "Submission Not Allowed",
        description: "Unfortunately, marks can no longer be submitted for this session.",
        variant: "destructive",
        
      });
      return;
    }

    if (!selectedCategory) {
      toast({
        title: "Category Required",
        description: "Please select an exam category before saving grades.",
        variant: "destructive",
        
      });
      return;
    }

    try {
      await saveGrades();
      toast({
        title: "Grades Saved",
        description: `Grades for ${selectedSubject} - ${categories.find(c => c.id === selectedCategory)?.name || 'Unknown Category'} have been saved.`,
        variant: "default",
        
      });
    } catch (error) {
      console.error('Error saving grades:', error);
      toast({
        title: "Save Failed",
        description: "Unable to save grades. Please try again.",
        variant: "destructive",
        
      });
    }
  };



  // DEV ONLY: Clear all grades for current selection from Firebase
  const handleClearGrades = async () => {
    if (!selectedSession?.id || !selectedSubjectId || !selectedExamTypeId || !selectedCategory) {
      toast({
        title: "Missing Selection",
        description: "Please select all required fields before clearing grades.",
        variant: "destructive",
      });
      return;
    }

    if (!confirm("⚠️ DEV MODE: Are you sure you want to delete all grades for this selection? This cannot be undone.")) {
      return;
    }

    try {
      const batch = writeBatch(db);
      let deleteCount = 0;

      // Delete grades for each student using deterministic document IDs
      for (const student of students) {
        const gradeDocId = generateGradeDocId(
          student.id,
          selectedSubjectId,
          selectedSession.id,
          selectedExamTypeId,
          selectedCategory,
          selectedYear
        );
        
        const gradeRef = doc(db, `schools/${schoolID}/grades`, gradeDocId);
        const gradeSnap = await getDoc(gradeRef);
        
        if (gradeSnap.exists()) {
          batch.delete(gradeRef);
          deleteCount++;
        }
      }

      if (deleteCount > 0) {
        await batch.commit();
        
        // Reset local state
        setStudents(prev => prev.map(s => ({
          ...s,
          marks: null,
          grade: 'N/A',
          remarks: '',
          gradeDocId: undefined
        })));
        setIsSubmitted(false);

        toast({
          title: "Grades Cleared",
          description: `Successfully deleted ${deleteCount} grade records.`,
          variant: "default",
        });
      } else {
        toast({
          title: "No Grades Found",
          description: "No grade records found to delete for this selection.",
          variant: "default",
        });
      }
    } catch (error) {
      console.error("Error clearing grades:", error);
      toast({
        title: "Clear Failed",
        description: "Unable to clear grades. Check console for details.",
        variant: "destructive",
      });
    }
  };

  // DEV ONLY: Clear ALL grades for the entire school from Firebase
  const handleClearAllGrades = async () => {
    if (!schoolID) {
      toast({
        title: "No School Selected",
        description: "Unable to determine school ID.",
        variant: "destructive",
      });
      return;
    }

    if (!confirm("⚠️ DEV MODE: Are you sure you want to delete ALL grades for the ENTIRE SCHOOL? This will delete grades for ALL students, ALL subjects, ALL sessions. This cannot be undone!")) {
      return;
    }

    if (!confirm("🚨 FINAL WARNING: This will permanently delete ALL grade records. Type 'yes' in the next prompt to confirm.")) {
      return;
    }

    const finalConfirm = prompt("Type 'DELETE ALL' to confirm deletion of ALL grades:");
    if (finalConfirm !== 'DELETE ALL') {
      toast({
        title: "Cancelled",
        description: "Grade deletion cancelled.",
        variant: "default",
      });
      return;
    }

    try {
      // Fetch all grades for the school
      const gradesCollection = collection(db, `schools/${schoolID}/grades`);
      const gradesSnapshot = await getDocs(gradesCollection);
      
      if (gradesSnapshot.empty) {
        toast({
          title: "No Grades Found",
          description: "No grade records found to delete.",
          variant: "default",
        });
        return;
      }

      // Delete in batches of 500 (Firestore limit)
      const docs = gradesSnapshot.docs;
      let deletedCount = 0;
      
      for (let i = 0; i < docs.length; i += 500) {
        const batch = writeBatch(db);
        const batchDocs = docs.slice(i, i + 500);
        
        for (const docSnap of batchDocs) {
          batch.delete(docSnap.ref);
          deletedCount++;
        }
        
        await batch.commit();
      }

      // Reset local state
      setStudents(prev => prev.map(s => ({
        ...s,
        marks: null,
        grade: 'N/A',

        gradeDocId: undefined
      })));
      setIsSubmitted(false);

      toast({
        title: "All Grades Cleared",
        description: `Successfully deleted ${deletedCount} grade records from the entire school.`,
        variant: "default",
      });
    } catch (error) {
      console.error("Error clearing all grades:", error);
      toast({
        title: "Clear Failed",
        description: "Unable to clear all grades. Check console for details.",
        variant: "destructive",
      });
    }
  };

  // DEV ONLY: Auto-fill random marks for all students
  const handleAutoFillMarks = () => {
    if (students.length === 0) {
      toast({
        title: "No Students",
        description: "Please load students first.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedCategory) {
      toast({
        title: "No Category Selected",
        description: "Please select a category first.",
        variant: "destructive",
      });
      return;
    }

    const maxMarks = categories.find(c => c.id === selectedCategory)?.maxMarks || 100;

    // Generate random marks with a bell curve distribution for more realistic grades
    setStudents(prev => prev.map(student => {
      // Generate a random mark using a simple bell curve (average around 60-70%)
      const random1 = Math.random();
      const random2 = Math.random();
      // Box-Muller transform for normal distribution
      const normalRandom = Math.sqrt(-2.0 * Math.log(random1)) * Math.cos(2.0 * Math.PI * random2);
      // Scale to our range: mean around 65%, std dev around 15%
      let mark = Math.round(65 + normalRandom * 15);
      const grade = calculateGrade(mark);
      
      return {
        ...student,
        marks: mark,
        grade
      };
    }));

    toast({
      title: "Marks Auto-Filled",
      description: `Generated random marks for ${students.length} students (0-${maxMarks}).`,
      variant: "default",
    });
  };

  const getGradeBadgeVariant = (grade: string) => {
    switch (grade) {
      case 'A': return 'default';
      case 'B': return 'secondary';
      case 'C': return 'secondary';
      case 'D': return 'secondary';
      case 'E': return 'destructive';
      default: return 'outline';
    }
  };

  const totalPages = Math.ceil(students.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentStudents = students.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFetchFormStudents = async (): Promise<Student[]> => {
    if (!selectedSubject || !selectedForm) {
      toast({
        title: "Missing Information",
        description: "Please select subject and class.",
        variant: "destructive",
      });
      return [];
    }

    const selectedSubjectItem = subjects.find(s => s.name === selectedSubject);
    if (!selectedSubjectItem) {
      toast({
        title: "Subject Not Found",
        description: "Selected subject not found.",
        variant: "destructive",
      });
      return [];
    }
    const targetSubjectId = selectedSubjectItem.subjectId;

    setAllFormStudents([]);
    setSelectedStudentIds([]);
    setSearchQuery('');
    setStudents([]);
    setIsSubmitted(false);
    setCurrentPage(1);

    setIsFetching(true);

    try {
      const formClasses = classes.filter(c => c.name.startsWith(selectedForm));
      if (formClasses.length === 0) {
    toast({
  title: "No Classes Found",
  description: `No classes found for ${selectedForm}.`,
  variant: "destructive",
  
    });
        return [];
      }

      const classUids = formClasses.map(c => c.classUid);
      let allStudents: Student[] = [];

      for (let i = 0; i < classUids.length; i += 10) {
        const batchUids = classUids.slice(i, i + 10);
        const studentsCollection = collection(db, `schools/${schoolID}/students`);
        const studentsQuery = query(studentsCollection, where("currentClassUid", "in", batchUids));
        const studentsSnapshot = await getDocs(studentsQuery);

        const batchStudentsRaw: Student[] = [];
        studentsSnapshot.docs.forEach((doc) => {
          const studentData = doc.data();
          const student: Student = {
            id: doc.id,
            name: `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim(),
            regNumber: studentData.registrationNumber || '',
            marks: null,
            grade: 'N/A',

            formClass: doc.data().currentClassUid ? classes.find(cls => cls.classUid === doc.data().currentClassUid)?.name || selectedForm : selectedForm,
            currentClassUid: doc.data().currentClassUid,
            enrolledSubjects: doc.data().enrolledSubjects || [],
            form: selectedForm.split(' ')[1] || '',
          };
          batchStudentsRaw.push(student);
        });

        const batchStudentsData = batchStudentsRaw.filter(s =>
          s.name &&
          s.regNumber &&
          s.enrolledSubjects?.includes(targetSubjectId)
        );
        allStudents = [...allStudents, ...batchStudentsData];
      }

      setAllFormStudents(allStudents);
      setHasFetchedFormStudents(true);

      // Always load students directly to grading interface
      setSelectedStudentIds(allStudents.map(s => s.id));
      setStudents(allStudents);
      setCurrentPage(1);
      setIsSubmitted(false);

      // If term, exam type and category are already selected, attempt to fetch existing grades
      if (selectedTerm && selectedExamType && selectedCategory) {
        try {
          const targetSubjectId = subjects.find(s => s.name === selectedSubject)?.subjectId || '';
          if (targetSubjectId && selectedSession?.id && selectedExamTypeId) {
            await fetchExistingGrades(
              allStudents.map(s => s.id),
              targetSubjectId,
              selectedSession.id,
              selectedExamTypeId
            );
            
            // Check completion status
            if (categories.length > 1) {
               checkCompletionStatus(
                  allStudents.map(s => s.id),
                  targetSubjectId,
                  selectedSession.id,
                  selectedExamTypeId,
                  categories
               );
            }
          }
        } catch (err) {
          console.error('Error loading existing grades after fetch:', err);
        }

        toast({
          title: "Students Loaded",
          description: `Loaded ${allStudents.length} enrolled students from ${selectedForm} for ${selectedSubject} and ready for grading.`,
          variant: 'default',
          
        });
      } else {
        // Set default values for grading if not already set
        let term = selectedTerm;
        let examType = selectedExamType;
        let category = selectedCategory;

        // Pick first available options as defaults
        if (!term && activeSessions.length > 0) term = activeSessions[0].name;
        if (!examType && activeSessions.length > 0 && activeSessions[0].examTypes.length > 0) examType = activeSessions[0].examTypes[0].name;
        if (!category && categories.length > 0) category = categories[0].id;

        setSelectedTerm(term);
        setSelectedExamType(examType);
        setSelectedCategory(category);

        toast({
          title: "Students Loaded",
          description: `Loaded ${allStudents.length} enrolled students from ${selectedForm} for ${selectedSubject} and ready for grading.`,
          variant: 'default',
          
        });
      }
    } catch (error) {
      console.error("Error fetching form students:", error);
      toast({
        title: "Fetch Failed",
        description: "Unable to load students. Check console for details.",
        variant: "destructive",
        
      });
    } finally {
      setIsFetching(false);
    }
    return students;
  };

  const toggleStudentSelection = (studentId: string) => {
    setSelectedStudentIds(prev =>
      prev.includes(studentId)
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedStudentIds.length === filteredFormStudents.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(filteredFormStudents.map(s => s.id));
    }
  };

  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) {
      toast({
        title: "Group Name Required",
        description: "Please enter a name for the group.",
        variant: "destructive",
      });
      return;
    }

    if (selectedStudentIds.length === 0) {
      toast({
        title: "No Students Selected",
        description: "Please select at least one student.",
        variant: "destructive",
      });
      return;
    }

    try {
      // If a group is selected we update it; otherwise create a new one in Firestore
      if (selectedGroup) {
        const groupRef = doc(db, `schools/${schoolID}/studentGroups`, selectedGroup);
        await updateDoc(groupRef, {
          name: newGroupName.trim(),
          subject: selectedSubject,
          form: selectedForm,
          studentIds: selectedStudentIds,
          updatedAt: serverTimestamp(),
        });

        setSavedGroups(prev => prev.map(g => g.id === selectedGroup ? { ...g, name: newGroupName.trim(), subject: selectedSubject, form: selectedForm, studentIds: selectedStudentIds } : g));

        toast({
          title: "Group Updated",
          description: `"${newGroupName}" has been updated.`,
          
        });
      } else {
        const groupsCollection = collection(db, `schools/${schoolID}/studentGroups`);
        const ref = await addDoc(groupsCollection, {
          name: newGroupName.trim(),
          subject: selectedSubject,
          form: selectedForm,
          studentIds: selectedStudentIds,
          createdAt: serverTimestamp(),
          createdBy: submittedBy,
        });

        const newGroup: StudentGroup = {
          id: ref.id,
          name: newGroupName.trim(),
          subject: selectedSubject,
          form: selectedForm,
          studentIds: selectedStudentIds,
        };

        setSavedGroups(prev => [...prev, newGroup]);
        setSelectedGroup(ref.id);

        toast({
          title: "Group Created",
          description: `"${newGroupName}" with ${selectedStudentIds.length} students created successfully.`,
          
        });
      }

      setShowGroupDialog(false);
      setNewGroupName('');
    } catch (error) {
      console.error('Error creating/updating group:', error);
  toast({ title: 'Failed to save group', description: 'Please try again.', variant: 'destructive' });
    }
  };

  const handleLoadGroup = async (groupId: string) => {
    const group = savedGroups.find(g => g.id === groupId);
    if (!group) return;

    // Set subject and form from group (and the matching subjectId)
    setSelectedSubject(group.subject);
    const groupSubject = subjects.find(s => s.name === group.subject);
    setSelectedSubjectId(groupSubject ? groupSubject.subjectId : '');
    setSelectedForm(group.form);
    setSelectedGroup(groupId);
    setNewGroupName(group.name || '');

    // Always be in subject grading when loading a saved group
    setGradingMode('subject');

    // Set required grading selection states if not set
    let term = selectedTerm;
    let examType = selectedExamType;
    let category = selectedCategory;
    let sessionForGroup: ExamSession | undefined = undefined;

    // If not set, pick first available
    if (!term && activeSessions.length > 0) term = activeSessions[0].name;
    if (!examType && activeSessions.length > 0 && activeSessions[0].examTypes.length > 0) examType = activeSessions[0].examTypes[0].name;
    if (!category && categories.length > 0) category = categories[0].id;

    sessionForGroup = activeSessions.find(s => s.name === term) || activeSessions[0];
    if (sessionForGroup) {
      setSelectedSession(sessionForGroup);
    }

    if (sessionForGroup && examType) {
      const et = sessionForGroup.examTypes.find(e => e.name === examType);
      setSelectedExamTypeId(et ? et.id : '');
    }

    setSelectedTerm(term);
    setSelectedExamType(examType);
    setSelectedCategory(category);

    // Fetch all students for the form/subject
    try {
      const selectedSubjectItem = groupSubject || subjects.find(s => s.name === group.subject);
      if (!selectedSubjectItem) {
        toast({
          title: "Subject Not Found",
          description: "Selected subject not found.",
          variant: "destructive",
        });
        return;
      }

      const formClasses = classes.filter(c => c.name.startsWith(group.form));
      if (formClasses.length === 0) {
        toast({
          title: "No Classes Found",
          description: `No classes found for ${group.form}.`,
          variant: "destructive",
        });
        return;
      }

      const classUids = formClasses.map(c => c.classUid);
      let allStudents: Student[] = [];

      for (let i = 0; i < classUids.length; i += 10) {
        const batchUids = classUids.slice(i, i + 10);
        const studentsCollection = collection(db, `schools/${schoolID}/students`);
        const studentsQuery = query(studentsCollection, where("currentClassUid", "in", batchUids));
        const studentsSnapshot = await getDocs(studentsQuery);

        const batchStudents = studentsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
            regNumber: data.registrationNumber || '',
            marks: null,
            grade: 'N/A',
            remarks: '',
            formClass: classes.find(cls => cls.classUid === data.currentClassUid)?.name || group.form,
            currentClassUid: data.currentClassUid,
            enrolledSubjects: data.enrolledSubjects || [],
            form: group.form.split(' ')[1] || '',
          } as Student;
        }).filter(s =>
          s.name &&
          s.regNumber &&
          s.enrolledSubjects?.includes(selectedSubjectItem.subjectId)
        );

        allStudents = [...allStudents, ...batchStudents];
      }

      // Filter to only group members and load directly to grading
      const groupStudents = allStudents.filter(s => group.studentIds.includes(s.id));
      
      setStudents(groupStudents);
      setSelectedStudentIds(group.studentIds);
      setCurrentPage(1);
      setIsSubmitted(false);

      // Load existing grades if all criteria are met
      const sessionId = sessionForGroup?.id;
      const selectedExamTypeItem = sessionForGroup?.examTypes.find(e => e.name === examType);
      if (term && examType && category && selectedSubjectItem.subjectId && sessionId && selectedExamTypeItem) {
        setSelectedExamTypeId(selectedExamTypeItem.id);
        setSelectedSubjectId(selectedSubjectItem.subjectId);
        
        // Mark that we're explicitly loading grades to skip the useEffect fetch
        skipGradesFetchRef.current = true;
        
        await fetchExistingGrades(
          groupStudents.map(s => s.id),
          selectedSubjectItem.subjectId,
          sessionId,
          selectedExamTypeItem.id,
          undefined,
          true // Suppress toast - we'll show our own "Group Loaded" toast
        );
        
        // Check completion status
        if (categories.length > 1) {
           // Note: categories might not be updated yet if we just set examType. 
           // But useEffect should handle it? 
           // Actually, handleLoadGroup runs async. 
           // Use the fetched categories or current state? 
           // Safest to rely on current categories state if it matches the loaded exam type.
           // However, if we just setExamType, categories state is stale.
           // We should probably rely on the user navigating or useEffect triggering?
           // For now, we attempt it with current categories if they match.
           checkCompletionStatus(
              groupStudents.map(s => s.id),
              selectedSubjectItem.subjectId,
              sessionId,
              selectedExamTypeItem.id,
              categories
           );
        }
      }

      toast({
        title: "Group Loaded",
        description: `Ready to grade "${group.name}" with ${groupStudents.length} students.`,
      });
    } catch (error) {
      console.error("Error loading group students:", error);
      toast({
        title: "Failed to Load Group",
        description: "Unable to load group students. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Removed unused group handlers (delete/update/start grading) that were not wired to UI

  const filteredFormStudents = allFormStudents.filter(student =>
    student.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.regNumber.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const calculateGroupStats = () => {
    const validMarks = students.filter(s => s.marks !== null).map(s => s.marks as number);
    if (validMarks.length === 0) return { average: 0, highest: 0, lowest: 0, total: 0 };

    return {
      average: Math.round(validMarks.reduce((a, b) => a + b, 0) / validMarks.length),
      highest: Math.max(...validMarks),
      lowest: Math.min(...validMarks),
      total: validMarks.length,
    };
  };

  const stats = calculateGroupStats();

  const activeExamTypes = useMemo(() =>
    selectedSession?.examTypes.filter(et => et.status === 'active').map(et => et.name) || [],
    [selectedSession]
  );

  const handleSubjectChange = useCallback((value: string) => {
    setSelectedSubject(value);
    const sub = subjects.find(s => s.name === value);
    setSelectedSubjectId(sub ? sub.subjectId : '');
  }, [subjects]);

  const handleExamTypeChange = useCallback((value: string) => {
    setSelectedExamType(value);
    if (selectedSession) {
      const et = selectedSession.examTypes.find(e => e.name === value);
      setSelectedExamTypeId(et ? et.id : '');
    }
    // Reset categories and selections when exam type changes
    setCategories([]);
    setSelectedCategory('');
    setStudents(prev => prev.map(student => ({
      ...student,
      marks: null,
      grade: 'N/A',
      remarks: '',
      gradeDocId: undefined
    })));
    setIsSubmitted(false);
  }, [selectedSession]);

  const handleCategoryChange = useCallback(async (categoryId: string) => {
    setSelectedCategory(categoryId);
    // Reset all student marks when changing categories
    setStudents(prev => prev.map(student => ({
      ...student,
      marks: null,
      grade: 'N/A',
      remarks: '',
      gradeDocId: undefined
    })));
    setIsSubmitted(false);

    // Fetch existing grades for the new category
    if (students.length > 0 && selectedSubjectId && selectedSession?.id && selectedExamTypeId) {
      setIsFetching(true);
      await fetchExistingGrades(
        students.map(s => s.id),
        selectedSubjectId,
        selectedSession.id,
        selectedExamTypeId,
        categoryId
      );
      
      // Check completion status on category change to ensure freshness
      if (categories.length > 1) {
          checkCompletionStatus(
             students.map(s => s.id),
             selectedSubjectId,
             selectedSession.id,
             selectedExamTypeId,
             categories
          );
      }

      setIsFetching(false);
    }
  }, [students, selectedSubjectId, selectedSession, selectedExamTypeId, fetchExistingGrades, checkCompletionStatus, selectedYear, categories]);

  // Fetch categories when subject or exam type changes
  useEffect(() => {
    if (!schoolID || !selectedSubjectId || !selectedExamTypeId) return;
    
    const fetchCategories = async () => {
      try {
        const categoriesCollection = collection(db, `schools/${schoolID}/examCategories`);
        const categoriesQuery = query(categoriesCollection, where("subjectId", "==", selectedSubjectId));
        const categoriesSnapshot = await getDocs(categoriesQuery);
        const categoriesData = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as ExamCategory));

        // Only keep categories for the selected exam type (or legacy ones without examTypeId)
        const byType = categoriesData.filter(cat => !cat.examTypeId || cat.examTypeId === selectedExamTypeId);
        
        // Prioritize session-specific categories to ensure history is preserved
        const sessionSpecific = byType.filter(cat => cat.examSessionId === selectedSession?.id);
        
        if (sessionSpecific.length > 0) {
          setCategories(sessionSpecific);
          if (!selectedCategory) setSelectedCategory(sessionSpecific[0].id);
        } else {
          // Fallback to global categories (no session ID) as templates
          const globalCats = byType.filter(cat => !cat.examSessionId);
          setCategories(globalCats);
          if (globalCats.length > 0 && !selectedCategory) setSelectedCategory(globalCats[0].id);
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
        toast({
          title: "Failed to Load Categories",
          description: "Unable to load exam categories.",
          variant: "destructive",
          
        });
      }
    };

    fetchCategories();
  }, [schoolID, selectedSubjectId, selectedExamTypeId, selectedCategory]);

  // Load saved student groups from Firestore for this school
  useEffect(() => {
    if (!schoolID) return;

    const fetchGroups = async () => {
      try {
        const groupsCollection = collection(db, `schools/${schoolID}/studentGroups`);
        const groupsSnapshot = await getDocs(groupsCollection);
        const groupsData: StudentGroup[] = groupsSnapshot.docs.map(g => ({ id: g.id, ...(g.data() as any) }));
        setSavedGroups(groupsData);
      } catch (error) {
        console.error('Error loading groups:', error);
        toast({ title: 'Failed to load groups', description: 'Unable to retrieve saved student groups.' , variant: 'destructive'});
      }
    };

    fetchGroups();
  }, [schoolID]);

  const handleSaveCategories = async () => {
    if (!selectedSubjectId || !selectedExamTypeId) return;

    // Validate total weight
    const totalWeight = tempCategories.reduce((sum, c) => sum + (c.weight || 0), 0);
    if (Math.abs(totalWeight - 100) > 0.1) {
      toast({
        title: "Invalid Weights",
        description: `Total weight must be 100%. Current total: ${totalWeight.toFixed(1)}%`,
        variant: "destructive",
      });
      return;
    }

    try {
      const batch = writeBatch(db);
      const categoriesCollection = collection(db, `schools/${schoolID}/examCategories`);

      // 1. Delete existing categories for this subject/examType/session
      // Only delete categories that belong to the current session to avoid wiping global/history
      categories.forEach(cat => {
        if (cat.examSessionId === selectedSession?.id) {
          const ref = doc(db, `schools/${schoolID}/examCategories`, cat.id);
          batch.delete(ref);
        }
      });

      // 2. Create new categories
      const newCategoriesList: ExamCategory[] = [];

      for (const temp of tempCategories) {
        if (!temp.name.trim()) continue;

        const ref = doc(categoriesCollection);
        const newCategory: any = {
          name: temp.name.trim(),
          subjectId: selectedSubjectId,
          maxMarks: temp.maxMarks,
          weight: temp.weight,
          examTypeId: selectedExamTypeId,
          examSessionId: selectedSession?.id,
          createdAt: serverTimestamp()
        };

        batch.set(ref, newCategory);
        
        newCategoriesList.push({
          id: ref.id,
          ...newCategory
        });
      }

      await batch.commit();
      setCategories(newCategoriesList);
      
      // Select first category and reset student state to prevent overwriting grades
      if (newCategoriesList.length > 0) {
        const firstCatId = newCategoriesList[0].id;
        setSelectedCategory(firstCatId);
        setStudents(prev => prev.map(student => ({
          ...student,
          marks: null,
          grade: 'N/A',
          remarks: '',
          gradeDocId: undefined
        })));
        setIsSubmitted(false);

        // Fetch grades for the auto-selected category if students are loaded
        if (students.length > 0 && selectedSubjectId && selectedSession?.id && selectedExamTypeId) {
          setIsFetching(true);
          await fetchExistingGrades(
            students.map(s => s.id),
            selectedSubjectId,
            selectedSession.id,
            selectedExamTypeId,
            firstCatId
          );
          setIsFetching(false);
        }
      } else {
        setSelectedCategory('');
      }

      setShowCategoryDialog(false);
      
      toast({
        title: "Categories Updated",
        description: "Exam categories have been saved successfully.",
        variant: "default",
      });

    } catch (error) {
      console.error("Error saving categories:", error);
      toast({
        title: "Save Failed",
        description: "Unable to save categories.",
        variant: "destructive",
      });
    }
  };



  // Show suspension warning if teacher is suspended




  return (
    <div className="flex flex-col flex-1 min-h-0 bg-[#FFFFFF] dark:bg-[#020817] font-plus-jakarta">
      <main className="flex-1 overflow-y-auto p-2 flex flex-col">
        <ContentLoader isLoading={isAuthLoading || !hasValidClaims} message="">
        <div className="space-y-2 m-2 flex-grow flex flex-col">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4 mb-2 m-2">
                <div className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 md:h-6 md:w-6 text-emerald-600" />
                  <h1 className="text-2xl md:text-3xl font-source-sans font-bold leading-tight text-gray-900 dark:text-white">
                    {userRole === 'admin' ? 'Grade Management' : 'Teacher Grading'}
                  </h1>
                </div>
                <div className="flex items-center gap-2 p-1 bg-gray-100 dark:bg-[#071021] rounded-lg">
                  <Button
                    variant={gradingMode === 'class' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setGradingMode('class')}
                    className={`gap-2 font-semibold text-sm ${gradingMode === 'class' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-transparent border border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]'}`}
                  >
                    <Users className="h-4 w-4" />
                    Class Grading
                  </Button>
                  <Button
                    variant={gradingMode === 'subject' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setGradingMode('subject')}
                    className={`gap-2 font-semibold text-sm ${gradingMode === 'subject' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-transparent border border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]'}`}
                  >
                    <BookOpen className="h-4 w-4" />
                    Subject Grading
                  </Button>
                  <Button
                    variant={gradingMode === 'projected' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setGradingMode('projected')}
                    className={`gap-2 font-semibold text-sm ${gradingMode === 'projected' ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-transparent border border-[#E2E8F0] dark:border-[#1E293B] text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#131E31]'}`}
                  >
                    <TrendingUp className="h-4 w-4" />
                    Projected Results
                  </Button>
                </div>
              </div>

              {gradingMode === 'class' && (
                <>
                  <Card className="border border-gray-200 dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Grade Entry Selection</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4 mb-4">
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Academic Year</label>
                          <Select value={selectedYear} onValueChange={setSelectedYear}>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="Select Year" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                              {years.map(year => (
                                <SelectItem key={year} value={year} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{year}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Class / Group</label>
                          <Select value={selectedClass} onValueChange={setSelectedClass}>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="Select Class" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                              {classes.map(cls => (
                                <SelectItem key={cls.id} value={cls.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{cls.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</label>
                          <Select value={selectedSubject} onValueChange={handleSubjectChange}>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="Select Subject" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                              {subjects.map(subject => (
                                <SelectItem key={subject.subjectId} value={subject.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{subject.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Term / Session</label>
                          <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="Select Term" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                              {activeSessions.map(session => (
                                <SelectItem key={session.id} value={session.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{session.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Exam Type</label>
                          <Select value={selectedExamType} onValueChange={handleExamTypeChange}>
                            <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                              <SelectValue placeholder="Select Exam Type" />
                            </SelectTrigger>
                            <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                              {activeExamTypes.map(type => (
                                <SelectItem key={type} value={type} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{type}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <Button onClick={handleFetchStudents} className="w-full md:w-auto font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB]" disabled={isFetching}>
                        {isFetching ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Loading...
                          </div>
                        ) : 'Fetch Students'}
                      </Button>
                    </CardContent>
                  </Card>

                  {students.length > 0 && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardHeader>
                        <div className="flex items-center justify-between">
                          <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                            Student Grades - {selectedClass} ({selectedSubject})
                          </CardTitle>
                          <div className="flex items-center gap-3">

                            <div className="flex items-center gap-2">
                              <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                                <SelectTrigger className="w-[200px] font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-9">
                                  <SelectValue placeholder="Select Category" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                  {categories.map(category => (
                                    <div key={category.id} className="flex items-center justify-between px-2 py-1.5">
                                      <SelectItem 
                                        value={category.id}
                                        className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31] flex-grow"
                                      >
                                        {category.name} ({category.maxMarks} marks)
                                      </SelectItem>
                                    </div>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setTempCategories(categories.map(c => ({ id: c.id, name: c.name, maxMarks: c.maxMarks, weight: c.weight || 0 })));
                                setShowCategoryDialog(true);
                              }}
                              className="h-9 border border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]"
                            >
                              Manage Categories
                            </Button>
                            {isTermClosed && (
                              <Badge variant="secondary" className="flex items-center gap-1">
                                <Lock className="h-3 w-3" />
                                Term Closed
                              </Badge>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isTermClosed && (
                          <div className="mb-4 p-3 bg-muted rounded-lg flex items-center gap-2 text-sm text-muted-foreground">
                            <AlertCircle className="h-4 w-4" />
                            This term is closed. Grades cannot be edited.
                          </div>
                        )}
                        <div className="overflow-x-auto relative">
                          {/* Loading overlay for smooth transitions */}
                          {isLoadingGrades && (
                            <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 flex items-center justify-center backdrop-blur-[1px] transition-opacity duration-200">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                Loading grades...
                              </div>
                            </div>
                          )}
                          <Table className={`font-source-sans transition-opacity duration-200 ${isLoadingGrades ? 'opacity-50' : 'opacity-100'}`}>
                            <TableHeader>
                              <TableRow className="border-b border-[#E2E8F0] dark:border-[#1E293B]">
                                <TableHead className="w-[150px] font-semibold text-gray-800 dark:text-gray-200 border-b border-[#E2E8F0] dark:border-[#1E293B]">Registration No.</TableHead>
                                <TableHead className="w-[200px] font-semibold text-gray-800 dark:text-gray-200 border-b border-[#E2E8F0] dark:border-[#1E293B]">Student Name</TableHead>
                                <TableHead className="w-[120px] font-semibold text-gray-800 dark:text-gray-200 border-b border-[#E2E8F0] dark:border-[#1E293B]">
                                  Marks (0-{categories.find(c => c.id === selectedCategory)?.maxMarks || 100})
                                </TableHead>
                                <TableHead className="w-[80px] font-semibold text-gray-800 dark:text-gray-200 border-b border-[#E2E8F0] dark:border-[#1E293B]">Grade</TableHead>

                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {currentStudents.map((student) => (
                                <TableRow key={student.id} className="border-b border-[#E2E8F0] dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#020817]">
                                  <TableCell className="font-plus-jakarta font-medium py-2 px-4 text-gray-800 dark:text-gray-200">{student.regNumber || 'N/A'}</TableCell>
                                  <TableCell className="font-plus-jakarta font-medium py-2 px-4 text-gray-800 dark:text-gray-200">
                                    <div className="flex items-center gap-2">
                                        <span>{student.name || 'N/A'}</span>
                                        {student.isIncomplete && (
                                            <div title="Incomplete Marks: Student has missing grades for some categories in this subject." className="flex items-center gap-1 bg-amber-100 text-amber-800 text-xs px-2 py-0.5 rounded-full border border-amber-200">
                                                <AlertCircle className="h-3 w-3" />
                                                <span className="hidden sm:inline">Incomplete</span>
                                            </div>
                                        )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="font-medium py-2 px-4">
                                    <Input
                                      id={`grade-input-class-${student.id}`}
                                      type="number"
                                      value={student.marks ?? ''}
                                      onChange={(e) => updateStudentMarks(student.id, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const currentIndex = currentStudents.findIndex(s => s.id === student.id);
                                          if (currentIndex !== -1 && currentIndex < currentStudents.length - 1) {
                                            const nextStudent = currentStudents[currentIndex + 1];
                                            const nextInput = document.getElementById(`grade-input-class-${nextStudent.id}`);
                                            nextInput?.focus();
                                          }
                                        }
                                      }}
                                      disabled={isTermClosed || isSubmitted}
                                      placeholder="Enter marks"
                                      min="0"
                                      max="100"
                                      className="w-full"
                                    />
                                  </TableCell>
                                  <TableCell className="font-medium py-2 px-4">
                                    <Badge variant={getGradeBadgeVariant(student.grade)}>
                                      {student.grade}
                                    </Badge>
                                  </TableCell>

                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        {students.length > itemsPerPage && (
                          <div className="mt-4 flex justify-center">
                            <Pagination>
                              <PaginationContent>
                                <PaginationItem>
                                  <PaginationPrevious
                                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                                </PaginationItem>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      onClick={() => handlePageChange(page)}
                                      isActive={currentPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                ))}
                                <PaginationItem>
                                  <PaginationNext
                                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                                </PaginationItem>
                              </PaginationContent>
                            </Pagination>
                          </div>
                        )}
                        {!isTermClosed && (
                          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
                            {/* Simplified Save Button */}
                            <Button 
                              onClick={handleSave}
                              className="bg-[#155DFC] hover:bg-[#1148CA] text-white"
                            >
                              <Save className="h-4 w-4 mr-2" />
                              Save Grades
                            </Button>
                            {/* DEV ONLY: Clear grades button for testing */}
                            <Button 
                              variant="outline" 
                              onClick={handleClearGrades}
                              className="border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Clear Grades (Dev)
                            </Button>
                            {/* DEV ONLY: Clear ALL grades button for testing */}
                            <Button 
                              variant="outline" 
                              onClick={handleClearAllGrades}
                              className="border-red-600 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950 dark:hover:bg-red-900 font-bold"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Clear ALL Grades (Dev)
                            </Button>
                            {/* DEV ONLY: Auto-fill marks button for testing */}
                            <Button 
                              variant="outline" 
                              onClick={handleAutoFillMarks}
                              className="border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950"
                            >
                              <TrendingUp className="h-4 w-4 mr-2" />
                              Auto Fill Marks (Dev)
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                  {students.length === 0 && selectedClass && selectedSubject && hasFetchedStudents && !isFetching && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardContent className="pt-6">
                        <div className="text-center text-muted-foreground py-12">
                          <GraduationCap className="h-16 w-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Students Found</p>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No students enrolled in {selectedClass} for {selectedSubject}.</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {isFetching && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardContent className="pt-6">
                        <div className="text-center text-muted-foreground py-12">
                          <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-lg font-semibold text-gray-900 dark:text-white">Loading students...</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {(!selectedClass || !selectedSubject) && !isFetching && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardContent className="pt-6">
                        <div className="text-center text-muted-foreground py-12">
                          <GraduationCap className="h-16 w-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Students Loaded</p>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Select a class, subject, term, and exam type, then click "Fetch Students" to begin grading.</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}
              {gradingMode === 'subject' && (
                <>
                  <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                    <CardHeader>
                      <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Subject Grading Setup</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Academic Year</label>
                            <Select value={selectedYear} onValueChange={setSelectedYear}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                <SelectValue placeholder="Select Year" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {years.map(year => (
                                  <SelectItem key={year} value={year} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{year}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Subject</label>
                            <Select value={selectedSubject} onValueChange={handleSubjectChange}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                <SelectValue placeholder="Select Subject" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {subjects.map(subject => (
                                  <SelectItem key={subject.subjectId} value={subject.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{subject.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Class</label>
                            <Select value={selectedForm} onValueChange={setSelectedForm}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                <SelectValue placeholder="Select Class" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {forms.map(form => (
                                  <SelectItem key={form} value={form} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{form}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Term / Session</label>
                            <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                <SelectValue placeholder="Select Term" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {activeSessions.map(session => (
                                  <SelectItem key={session.id} value={session.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{session.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Exam Type</label>
                            <Select value={selectedExamType} onValueChange={handleExamTypeChange}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                <SelectValue placeholder="Select Exam Type" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {activeExamTypes.map(type => (
                                  <SelectItem key={type} value={type} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{type}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Category</label>
                            <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                              <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                <SelectValue placeholder="Select Category" />
                              </SelectTrigger>
                              <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                {categories.map(category => (
                                  <SelectItem key={category.id} value={category.id} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{category.name} ({category.maxMarks} marks)</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      <Button onClick={handleFetchFormStudents} className="w-full md:w-auto mt-2 font-semibold bg-[#155DFC] text-white hover:bg-[#2563EB]" disabled={isFetching}>
                        {isFetching ? (
                          <div className="flex items-center gap-2">
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                            Loading...
                          </div>
                        ) : 'Load Students'}
                      </Button>
                    </CardContent>
                  </Card>
                  {savedGroups.length > 0 && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardHeader>
                        <div className="flex items-center justify-between w-full gap-3">
                          <CardTitle className="text-lg font-semibold text-gray-900 dark:text-white">Saved Groups</CardTitle>
                          <div className="flex-1 flex justify-center px-4">
                            <div className="relative w-full max-w-md">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                placeholder="Search groups..."
                                value={groupSearchQuery}
                                onChange={(e) => setGroupSearchQuery(e.target.value)}
                                className="pl-10 w-full h-9"
                              />
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700 dark:border-emerald-400 dark:text-emerald-400 dark:hover:bg-emerald-950 dark:hover:text-emerald-300 font-medium transition-colors duration-200"
                            onClick={() => navigate('/groups-management')}
                          >
                            <BookOpen className="h-4 w-4 mr-2" />
                            View More
                          </Button>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {filteredSavedGroups.length > 0 ? (
                            filteredSavedGroups.map(group => (
                              <div key={group.id} className="flex items-center justify-between p-3 border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg bg-[#FFFFFF] dark:bg-[#020817] hover:shadow-sm transition-shadow">
                                <div>
                                  <p className="font-semibold text-gray-900 dark:text-white">{group.name}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {group.subject} • {group.form} • {group.studentIds.length} students
                                  </p>
                                </div>
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleLoadGroup(group.id)}
                                  >
                                    Load
                                  </Button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center p-6 text-muted-foreground">No groups match your search.</div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {students.length > 0 && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardHeader>
                        <div className="flex flex-col gap-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-xl font-semibold text-gray-900 dark:text-white">
                              Grade Entry - {selectedSubject} ({selectedForm})
                            </CardTitle>
                            <div className="flex items-center gap-3">

                              <div className="flex items-center gap-2">
                                <Select value={selectedCategory} onValueChange={handleCategoryChange}>
                                  <SelectTrigger className="w-[200px] font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-9">
                                    <SelectValue placeholder="Select Category" />
                                  </SelectTrigger>
                                  <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                    {categories.map(category => (
                                      <div key={category.id} className="flex items-center justify-between px-2 py-1.5">
                                        <SelectItem 
                                          value={category.id}
                                          className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31] flex-grow"
                                        >
                                          {category.name} ({category.maxMarks} marks)
                                        </SelectItem>
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          className="h-8 px-2 ml-2"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setTempCategories(categories.map(c => ({ id: c.id, name: c.name, maxMarks: c.maxMarks, weight: c.weight || 0 })));
                                            setShowCategoryDialog(true);
                                          }}
                                        >
                                          Edit
                                        </Button>
                                      </div>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setTempCategories(categories.map(c => ({ id: c.id, name: c.name, maxMarks: c.maxMarks, weight: c.weight || 0 })));
                                  setShowCategoryDialog(true);
                                }}
                                className="h-9 border border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31]"
                              >
                                New Category
                              </Button>
                              {isTermClosed && (
                                <Badge variant="secondary" className="flex items-center gap-1">
                                  <Lock className="h-3 w-3" />
                                  Term Closed
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Academic Year</label>
                              <Select value={selectedYear} onValueChange={setSelectedYear}>
                                <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                  <SelectValue placeholder="Select Year" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                  {years.map(year => (
                                    <SelectItem key={year} value={year} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{year}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Term / Session</label>
                              <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                                <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                  <SelectValue placeholder="Select Term" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                  {activeSessions.map(session => (
                                    <SelectItem key={session.id} value={session.name} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{session.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Exam Type</label>
                              <Select value={selectedExamType} onValueChange={handleExamTypeChange}>
                                <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                                  <SelectValue placeholder="Select Exam Type" />
                                </SelectTrigger>
                                <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B]">
                                  {activeExamTypes.map(type => (
                                    <SelectItem key={type} value={type} className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]">{type}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          {stats.total > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                              <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-medium text-muted-foreground">Average</p>
                                <p className="text-2xl font-bold text-primary">{stats.average}%</p>
                              </div>
                              <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-medium text-muted-foreground">Highest</p>
                                <p className="text-2xl font-bold text-green-600">{stats.highest}%</p>
                              </div>
                              <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-medium text-muted-foreground">Lowest</p>
                                <p className="text-2xl font-bold text-orange-600">{stats.lowest}%</p>
                              </div>
                              <div className="p-3 bg-muted rounded-lg">
                                <p className="text-sm font-medium text-muted-foreground">Graded</p>
                                <p className="text-2xl font-bold">{stats.total}/{students.length}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        {isTermClosed && (
                          <div className="mb-4 p-3 bg-muted rounded-lg flex items-center gap-2 text-sm text-muted-foreground">
                            <AlertCircle className="h-4 w-4" />
                            This term is closed. Grades cannot be edited.
                          </div>
                        )}
                        <div className="overflow-x-auto relative">
                          {/* Loading overlay for smooth transitions */}
                          {isLoadingGrades && (
                            <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 flex items-center justify-center backdrop-blur-[1px] transition-opacity duration-200">
                              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                Loading grades...
                              </div>
                            </div>
                          )}
                          <Table className={`transition-opacity duration-200 ${isLoadingGrades ? 'opacity-50' : 'opacity-100'}`}>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[120px]">Reg. No.</TableHead>
                                <TableHead className="w-[200px]">Student Name</TableHead>
                                <TableHead className="w-[100px]">Stream</TableHead>
                                <TableHead className="w-[120px]">
                                  Marks (0-{categories.find(c => c.id === selectedCategory)?.maxMarks || 100})
                                </TableHead>
                                <TableHead className="w-[80px]">Grade</TableHead>

                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {currentStudents.map((student) => (
                                <TableRow key={student.id} className="border-b border-gray-200 dark:border-[#1E293B] hover:bg-[#F8FAFC] dark:hover:bg-[#020817]">
                                  <TableCell className="font-plus-jakarta font-medium py-2 px-4 text-gray-800 dark:text-gray-200">{student.regNumber || 'N/A'}</TableCell>
                                  <TableCell className="font-plus-jakarta font-medium py-2 px-4 text-gray-800 dark:text-gray-200">{student.name || 'N/A'}</TableCell>
                                  <TableCell className="font-plus-jakarta font-medium py-2 px-4">
                                    <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-800">{student.formClass}</Badge>
                                  </TableCell>
                                  <TableCell className="font-plus-jakarta font-medium py-2 px-4">
                                    <Input
                                      id={`grade-input-subject-${student.id}`}
                                      type="number"
                                      value={student.marks ?? ''}
                                      onChange={(e) => updateStudentMarks(student.id, e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          e.preventDefault();
                                          const currentIndex = currentStudents.findIndex(s => s.id === student.id);
                                          if (currentIndex !== -1 && currentIndex < currentStudents.length - 1) {
                                            const nextStudent = currentStudents[currentIndex + 1];
                                            const nextInput = document.getElementById(`grade-input-subject-${nextStudent.id}`);
                                            nextInput?.focus();
                                          }
                                        }
                                      }}
                                      disabled={isTermClosed || isSubmitted}
                                      placeholder="Enter marks"
                                      min="0"
                                      max="100"
                                      className="w-full font-source-sans"
                                    />
                                  </TableCell>
                                  <TableCell className="font-plus-jakarta font-medium py-2 px-4">
                                    <Badge variant={getGradeBadgeVariant(student.grade)} className="font-source-sans">
                                      {student.grade}
                                    </Badge>
                                  </TableCell>

                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                        {students.length > itemsPerPage && (
                          <div className="mt-4 flex justify-center">
                            <Pagination>
                              <PaginationContent>
                                <PaginationItem>
                                  <PaginationPrevious
                                    onClick={() => currentPage > 1 && handlePageChange(currentPage - 1)}
                                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                                </PaginationItem>
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                  <PaginationItem key={page}>
                                    <PaginationLink
                                      onClick={() => handlePageChange(page)}
                                      isActive={currentPage === page}
                                      className="cursor-pointer"
                                    >
                                      {page}
                                    </PaginationLink>
                                  </PaginationItem>
                                ))}
                                <PaginationItem>
                                  <PaginationNext
                                    onClick={() => currentPage < totalPages && handlePageChange(currentPage + 1)}
                                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                                  />
                                </PaginationItem>
                              </PaginationContent>
                            </Pagination>
                          </div>
                        )}
                        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-end">
                            {/* Simplified Save Button */}
                            <Button 
                              onClick={handleSave}
                              className="bg-[#155DFC] hover:bg-[#1148CA] text-white"
                            >
                              <Save className="h-4 w-4 mr-2" />
                              Save Grades
                            </Button>
                            <Button variant="outline">
                                <Download className="h-4 w-4 mr-2" />
                                Export Grades
                            </Button>
                          {/* DEV ONLY: Clear grades button for testing */}
                          <Button 
                            variant="outline" 
                            onClick={handleClearGrades}
                            className="border-red-500 text-red-500 hover:bg-red-50 dark:hover:bg-red-950"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Clear Grades (Dev)
                          </Button>
                          {/* DEV ONLY: Clear ALL grades button for testing */}
                          <Button 
                            variant="outline" 
                            onClick={handleClearAllGrades}
                            className="border-red-600 bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-950 dark:hover:bg-red-900 font-bold"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Clear ALL Grades (Dev)
                          </Button>
                          {/* DEV ONLY: Auto-fill marks button for testing */}
                          <Button 
                            variant="outline" 
                            onClick={handleAutoFillMarks}
                            className="border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950"
                          >
                            <TrendingUp className="h-4 w-4 mr-2" />
                            Auto Fill Marks (Dev)
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {allFormStudents.length === 0 && selectedSubject && selectedForm && hasFetchedFormStudents && !isFetching && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardContent className="pt-6">
                        <div className="text-center text-muted-foreground py-12">
                          <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Students Found</p>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">No students enrolled in classes under {selectedForm} for {selectedSubject}.</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {isFetching && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardContent className="pt-6">
                        <div className="text-center text-muted-foreground py-12">
                          <p className="text-lg font-semibold text-gray-900 dark:text-white">Loading students...</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {(!selectedSubject || !selectedForm) && !isFetching && (
                    <Card className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] m-2">
                      <CardContent className="pt-6">
                        <div className="text-center text-muted-foreground py-12">
                          <BookOpen className="h-16 w-16 mx-auto mb-4 opacity-50" />
                          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Students Loaded</p>
                          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Select a subject and class, then click "Load Students" to begin.</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              )}

              {gradingMode === 'projected' && (
                <ProjectedResults schoolID={schoolID} />
              )}

              <Dialog open={showGroupDialog} onOpenChange={setShowGroupDialog}>
                <DialogContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] max-w-3xl">
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                      {selectedGroup ? 'Edit Student Group' : 'Create Student Group'}
                    </DialogTitle>
                    <DialogDescription className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {selectedGroup ? 'Modify the group details and member list' : 'Create a reusable group'} for {selectedSubject}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <div className="space-y-2 m-2">
                      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Group Name</label>
                      <Input
                        placeholder="e.g., Mr. Gilbert's Computer Students"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B]"
                      />
                    </div>
                    <div className="space-y-2 m-2">
                      <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Select Class</label>
                      <div className="relative">
                        <Select 
                          value={selectedForm} 
                          onValueChange={async (value) => {
                            // Clear everything first
                            setAllFormStudents([]);
                            setSelectedStudentIds([]);
                            setIsFetching(true);
                            
                            try {
                              // Set form after clearing to ensure clean state
                              setSelectedForm(value);
                              
                              // Force a fresh load of students by clearing the cache first
                              const formClasses = classes.filter(c => c.name.startsWith(value));
                              if (formClasses.length === 0) {
                                setAllFormStudents([]);
                                return;
                              }
                              
                              const classUids = formClasses.map(c => c.classUid);
                              let allStudents: Student[] = [];

                              // Load students in batches
                              for (let i = 0; i < classUids.length; i += 10) {
                                const batchUids = classUids.slice(i, i + 10);
                                const studentsCollection = collection(db, `schools/${schoolID}/students`);
                                const studentsQuery = query(
                                  studentsCollection, 
                                  where("currentClassUid", "in", batchUids)
                                );
                                const studentsSnapshot = await getDocs(studentsQuery);

                                const batchStudents = studentsSnapshot.docs.map(doc => {
                                  const data = doc.data();
                                  return {
                                    id: doc.id,
                                    name: `${data.firstName || ''} ${data.lastName || ''}`.trim(),
                                    regNumber: data.registrationNumber || '',
                                    marks: null,
                                    grade: 'N/A',

                                    formClass: classes.find(cls => cls.classUid === data.currentClassUid)?.name || value,
                                    currentClassUid: data.currentClassUid,
                                    enrolledSubjects: data.enrolledSubjects || [],
                                    form: value
                                  } as Student;
                                }).filter(s => 
                                  s.name && 
                                  s.regNumber && 
                                  s.enrolledSubjects?.includes(selectedSubjectId)
                                );
                                
                                allStudents = [...allStudents, ...batchStudents];
                              }

                              // Update state with fresh data
                              setAllFormStudents(allStudents);

                              // If editing, restore valid selections
                              if (selectedGroup) {
                                const savedIds = savedGroups.find(g => g.id === selectedGroup)?.studentIds || [];
                                const validIds = savedIds.filter(id => 
                                  allStudents.some(s => s.id === id)
                                );
                                setSelectedStudentIds(validIds);
                              }
                            } catch (error) {
                              console.error('Error loading students:', error);
                              toast({
                                title: 'Error Loading Students',
                                description: 'Failed to load students. Please try again.',
                                variant: 'destructive',
                              });
                            } finally {
                              setIsFetching(false);
                            }
                          }}
                        >
                          <SelectTrigger className="font-medium bg-white dark:bg-[#020817] text-black dark:text-white border-[#E2E8F0] dark:border-[#1E293B] hover:bg-gray-50 dark:hover:bg-[#131E31] h-10">
                            <SelectValue placeholder="Select Class" />
                          </SelectTrigger>
                          <SelectContent className="bg-white dark:bg-[#020817] border-[#E2E8F0] dark:border-[#1E293B] max-h-[300px]">
                            <div className="sticky top-0 p-2 bg-white dark:bg-[#020817] border-b border-[#E2E8F0] dark:border-[#1E293B]">
                              <Input
                                placeholder="Search classes..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="h-8"
                              />
                            </div>
                            <div className="overflow-y-auto">
                              {forms
                                .filter(form => form.toLowerCase().includes(searchQuery.toLowerCase()))
                                .map(form => (
                                  <SelectItem 
                                    key={form} 
                                    value={form} 
                                    className="font-medium text-black dark:text-white hover:bg-gray-100 dark:hover:bg-[#131E31] focus:bg-gray-100 dark:focus:bg-[#131E31]"
                                  >
                                    {form}
                                  </SelectItem>
                              ))}
                            </div>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {selectedForm && allFormStudents.length > 0 && (
                      <div className="space-y-3 m-2">
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              Group Members ({selectedStudentIds.length})
                            </label>
                            <div className="relative">
                              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                              <Input
                                placeholder="Search students by name or reg no..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-10 w-[300px]"
                              />
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <div className="relative flex-1">
                              <Input
                                placeholder="Add student by registration number..."
                                className="pr-24"
                                onKeyDown={async (e) => {
                                  if (e.key === 'Enter') {
                                    const regNumber = e.currentTarget.value.trim();
                                    if (!regNumber) return;
                                    
                                    setIsFetching(true);
                                    try {
                                      const studentsCollection = collection(db, `schools/${schoolID}/students`);
                                      const studentQuery = query(
                                        studentsCollection,
                                        where("registrationNumber", "==", regNumber)
                                      );
                                      const studentSnapshot = await getDocs(studentQuery);
                                      
                                      if (studentSnapshot.empty) {
                                        toast({
                                          title: "Student Not Found",
                                          description: `No student found with registration number ${regNumber}`,
                                          variant: "destructive",
                                        });
                                        return;
                                      }

                                      const studentDoc = studentSnapshot.docs[0];
                                      const studentData = studentDoc.data();
                                      
                                      // Check if student is in a valid class level
                                      const studentClass = classes.find(c => c.classUid === studentData.currentClassUid);
                                      if (!studentClass || !studentClass.name.startsWith(selectedForm)) {
                                        toast({
                                          title: "Invalid Class",
                                          description: `Student is not in ${selectedForm}`,
                                          variant: "destructive",
                                        });
                                        return;
                                      }

                                      // Check if student is enrolled in the subject
                                      if (!studentData.enrolledSubjects?.includes(selectedSubjectId)) {
                                        toast({
                                          title: "Not Enrolled",
                                          description: `Student is not enrolled in ${selectedSubject}`,
                                          variant: "destructive",
                                        });
                                        return;
                                      }

                                      // Add student to the list if not already present
                                      const newStudent: Student = {
                                        id: studentDoc.id,
                                        name: `${studentData.firstName || ''} ${studentData.lastName || ''}`.trim(),
                                        regNumber: studentData.registrationNumber || '',
                                        marks: null,
                                        grade: 'N/A',

                                        formClass: studentClass.name,
                                        currentClassUid: studentData.currentClassUid,
                                        enrolledSubjects: studentData.enrolledSubjects || [],
                                        form: selectedForm
                                      };

                                      if (!allFormStudents.some(s => s.id === newStudent.id)) {
                                        setAllFormStudents(prev => [...prev, newStudent]);
                                      }

                                      if (!selectedStudentIds.includes(newStudent.id)) {
                                        setSelectedStudentIds(prev => [...prev, newStudent.id]);
                                        toast({
                                          title: "Student Added",
                                          description: `${newStudent.name} has been added to the group`,
                                          variant: "default",
                                        });
                                      }

                                      // Clear the input
                                      e.currentTarget.value = '';
                                    } finally {
                                      setIsFetching(false);
                                    }
                                  }
                                }}
                              />
                              <div className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
                                Press Enter
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="border border-[#E2E8F0] dark:border-[#1E293B] rounded-lg">
                          <div className="p-2 border-b border-[#E2E8F0] dark:border-[#1E293B] bg-muted/50">
                            <div className="flex items-center gap-2">
                              <Checkbox
                                checked={selectedStudentIds.length === filteredFormStudents.length && filteredFormStudents.length > 0}
                                onCheckedChange={toggleSelectAll}
                                className="border-[#E2E8F0] dark:border-[#1E293B]"
                              />
                              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                Select All Students
                              </label>
                            </div>
                          </div>
                          <div className="max-h-[300px] overflow-y-auto p-2 space-y-1">
                            {filteredFormStudents.map(student => (
                              <div
                                key={student.id}
                                className="flex items-center gap-2 p-2 hover:bg-muted/50 rounded-lg transition-colors"
                              >
                                <Checkbox
                                  checked={selectedStudentIds.includes(student.id)}
                                  onCheckedChange={() => toggleStudentSelection(student.id)}
                                  className="border-[#E2E8F0] dark:border-[#1E293B]"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-gray-900 dark:text-white text-sm truncate">
                                    {student.name}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {student.regNumber} • {student.formClass}
                                  </p>
                                </div>
                              </div>
                            ))}
                            {filteredFormStudents.length === 0 && (
                              <div className="text-center p-4 text-muted-foreground">
                                No students found matching the search criteria
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedForm && allFormStudents.length === 0 && (
                      <div className="text-center p-6 bg-muted/50 rounded-lg m-2">
                        <div className="flex items-center justify-center gap-2">
                          {isFetching ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-500 border-t-transparent"></div>
                              <p className="text-sm text-muted-foreground">
                                Loading students for {selectedForm}...
                              </p>
                            </>
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              No students found in {selectedForm}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={() => {
                      setShowGroupDialog(false);
                      setAllFormStudents([]);
                      setSelectedStudentIds([]);
                      setSearchQuery('');
                    }}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleCreateGroup} 
                      className="bg-[#155DFC] hover:bg-[#1148CA] text-white"
                      disabled={!selectedForm || selectedStudentIds.length === 0}
                    >
                      {selectedGroup ? 'Update Group' : 'Create Group'}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
                <DialogContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817]">
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">Grades Submitted Successfully</DialogTitle>
                    <DialogDescription className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      All grades for {selectedClass || selectedForm} - {selectedSubject} ({selectedExamType}) have been submitted successfully.
                      Students and parents will be notified.
                    </DialogDescription>
                  </DialogHeader>
                  <Button onClick={() => setShowSuccessDialog(false)}>
                    Close
                  </Button>
                </DialogContent>
              </Dialog>

              <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
                <DialogContent className="border border-[#E2E8F0] dark:border-[#1E293B] bg-[#FFFFFF] dark:bg-[#020817] max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-white">
                      Manage Exam Categories
                    </DialogTitle>
                    <DialogDescription className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      Configure the papers/sections for {selectedSubject} - {selectedExamType}.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="rounded-md border border-[#E2E8F0] dark:border-[#1E293B]">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Category Name</TableHead>
                            <TableHead className="w-[100px]">Max Marks</TableHead>
                            <TableHead className="w-[100px]">Weight (%)</TableHead>
                            <TableHead className="w-[50px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tempCategories.map((cat, index) => {
                            return (
                              <TableRow key={index}>
                                <TableCell>
                                  <Input
                                    value={cat.name}
                                    onChange={(e) => {
                                      const newCats = [...tempCategories];
                                      newCats[index].name = e.target.value;
                                      setTempCategories(newCats);
                                    }}
                                    placeholder="e.g. Paper 1"
                                    className="h-8"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    value={cat.maxMarks}
                                    onChange={(e) => {
                                      const newCats = [...tempCategories];
                                      newCats[index].maxMarks = Number(e.target.value);
                                      setTempCategories(newCats);
                                    }}
                                    className="h-8"
                                    min="1"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    value={cat.weight}
                                    onChange={(e) => {
                                      const newCats = [...tempCategories];
                                      newCats[index].weight = Number(e.target.value);
                                      setTempCategories(newCats);
                                    }}
                                    className="h-8"
                                    min="0"
                                    max="100"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-500 hover:text-red-600"
                                    onClick={() => {
                                      const newCats = tempCategories.filter((_, i) => i !== index);
                                      setTempCategories(newCats);
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setTempCategories([...tempCategories, { name: '', maxMarks: 100, weight: 0 }])}
                      className="w-full border-dashed mt-2"
                    >
                      <Plus className="mr-2 h-4 w-4" /> Add Category
                    </Button>

                    <div className="flex justify-between text-sm text-muted-foreground px-2">
                      <span>Total Marks: {tempCategories.reduce((sum, c) => sum + (c.maxMarks || 0), 0)}</span>
                      <span className={Math.abs(tempCategories.reduce((sum, c) => sum + (c.weight || 0), 0) - 100) > 0.1 ? "text-red-500 font-bold" : "text-green-600 font-bold"}>
                        Total Weight: {tempCategories.reduce((sum, c) => sum + (c.weight || 0), 0)}% / 100%
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end">
                    <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSaveCategories} 
                      className="bg-[#155DFC] hover:bg-[#1148CA] text-white"
                    >
                      Save Configuration
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            </ContentLoader>
          </main>
        </div>
  );
};

export default SharedGradingDashboard;
